<template>
    <div class="job-preferences-component" data-cy="profile-view-job-preferences">
        <div class="left">
            <div v-if="isViewing && candidate.brandId === BrandId.BigFour">
                <span data-cy="profile-view-willingness-to-change" class="p-float-label">
                    <span>{{getDisplayFriendlyWillingnessToChange()}}</span>
                    <label>{{t("willingnesstochange")}}</label>
                </span>
            </div>
            <div v-if="isViewing">
                <span data-cy="profile-view-working-time" class="p-float-label employment-type">
                    <span>
                        <ul class="checklist" data-cy="profile-view-remote">
                            <li> <i class="pi pi-check" />{{getDisplayFriendlyFulltime()}}</li>
                            <li v-for="remote in editableCandidate.remote" :key="remote">
                                <i class="pi pi-check" /> {{getDisplayFriendlyRemote(remote)}}
                            </li>
                        </ul>
                    </span>
                    <label>{{t("type.of.employment")}}</label>
                </span>
            </div>
            <div v-else class="add-space">
                <h5 class="p-mt-0">
                    {{t("job.preferences.willingnesstochange")}}
                </h5>
                <span class="p-float-label">
                    <Dropdown :options="changeOptions"
                              v-model="changeOptionSelection"
                              optionLabel="name"
                              optionValue="value"
                              data-cy="job-preferences-dropdown"/>
                    <label>{{t("job.preferences.choose.willingnesstochange")}}</label>
                </span>
                <span class="p-no-float-label">
                    <h5>{{t("job.preferences.changemotivations")}}</h5>
                    <ul class="tags"
                        v-if="editableCandidate.changeMotivations?.length && editableCandidate.changeMotivations.length>0">
                        <li v-for="x in editableCandidate.changeMotivations" :key="x.id">
                            {{t("job.preferences.changemotivations." + x.id)}}
                            <Button icon="pi pi-times"
                                    class="p-button-rounded p-button-outlined"
                                    @click="handleMotivationRemoval($event, x)" />
                        </li>
                    </ul>
                    <MultiSelect v-model="v$.changeMotivations.$model"
                                 display="none"
                                 :class="{ 'p-invalid': v$.changeMotivations.$error }"
                                 :options="motivationOptions"
                                 optionLabel="name"
                                 data-cy="job-preferences-change-motivation">
                    </MultiSelect>
                    <label>{{t("job.preferences.choose.changemotivation")}}</label>
                    <small class="p-error p-mt-0"
                           v-for="(error, index) of v$.changeMotivations.$errors"
                           :key="index">
                        {{error.$message}}
                    </small>
                </span>
                <h5 class="p-mt-2">
                    {{t("job.preferences.type.of.employment")}}
                    <Tooltip :message="t('job.preferences.type.of.employment.tooltip')" />
                </h5>
                <span class="p-float-label">
                    <Dropdown :options="fulltimeOptions"
                              v-model="fullTimeOptionSelection"
                              optionLabel="name"
                              data-cy="profile-edit-working-time"
                              optionValue="value" />
                    <label>{{t("job.preferences.choose.work.time")}}</label>
                </span>

                <span class="p-no-float-label">
                    <ul data-cy="profile-edit-homeoffice"
                        class="tags"
                        v-if="editableCandidate.remote?.length && editableCandidate.remote.length > 0">
                        <li v-for="remote in editableCandidate.remote" :key="remote"
                            data-cy="profile-edit-homeoffice-chosen">
                            {{getDisplayFriendlyRemote(remote)}}
                            <Button icon="pi pi-times"
                                    class="p-button-rounded p-button-outlined"
                                    data-cy="profile-edit-homeoffice-remove"
                                    @click="handleHomeOfficeRemoval($event, remote)" />
                        </li>
                    </ul>
                    <MultiSelect id="remote"
                                 :options="remoteOptions"
                                 display="none"
                                 :class="{ 'p-invalid': v$.remote.$error }"
                                 v-model="v$.remote.$model"
                                 optionLabel="name"
                                 data-cy="profile-edit-homeoffice-list"
                                 optionValue="value" />

                    <label>{{t("job.preferences.choose.home.office.options")}}</label>
                </span>
                <small class="p-error p-mt-0"
                       v-for="(error, index) of v$.remote.$errors"
                       :key="index">
                    {{error.$message}}
                </small>
            </div>
        </div>
        <div class="right">
            <div v-if="editableCandidate.remote?.length === 0 || editableCandidate.remote?.find(ho => ho !== HomeOffice.Mandatory)">
                <div v-if="isViewing">
                    <span data-cy="profile-view-search-radius" class="p-float-label">
                        <span>
                            <i class="pi pi-map" /> {{editableCandidate.searchRadius}} km
                            {{t("around")}}
                            <span v-if="editableCandidate.zipCode">
                                {{editableCandidate.zipCode}}
                            </span>
                            <span v-else>
                                <i class="pi pi-times" />
                                {{t("profile.multiselect.nothingselected")}}
                            </span>
                        </span>
                        <label>
                            {{t("job.preferences.choose.searchradius")}}
                            <Tooltip :message="t('job.preferences.choose.commute.tooltip')" />
                        </label>
                    </span>
                </div>
                <div v-else class="travel-slider">
                    <span data-cy="profile-edit-search-radius" class="p-float-label">
                        <h5 class="p-mt-2">
                            {{t("job.preferences.choose.commute")}}
                            <Tooltip :message="t('job.preferences.choose.commute.tooltip')" />
                        </h5>
                        <Slider v-model="editableCandidate.searchRadius"
                                :step="1"
                                :min="1"
                                :max="100"
                                @change="validateSlider" />
                    </span>
                </div>
            </div>

            <div v-if="isViewing">
                <span data-cy="profile-view-relocate" class="p-float-label">
                    <ul class="tags" data-cy="profile-view-relocate-options"
                        v-if="computedRelocations && computedRelocations.length > 0">
                        <li v-for="option in computedRelocations" :key="option.name">
                            {{option.name}}
                        </li>
                    </ul>
                    <span v-else>
                        <i class="pi pi-times" />
                        {{t("profile.multiselect.nothingselected")}}
                    </span>
                    <label>
                        {{t("job.preferences.choose.more.cities")}}
                        <Tooltip :message="t('job.preferences.choose.more.cities')" />
                    </label>
                </span>
            </div>
            <div v-else>
                <span data-cy="profile-edit-relocate-radio" class="p-no-float-label relocate">
                    <h5>{{t("job.preferences.choose.move.to.another.city")}}</h5>
                    <div class="relocate-options">
                        <div>
                            <RadioButton name="relocate" data-cy="wizard-relocate-yes" :value="true"
                                         v-model="relocate" /> {{t("yes")}}
                        </div>
                        <div>
                            <RadioButton name="relocate" data-cy="wizard-relocate-no" :value="false"
                                         v-model="relocate" /> {{t("no")}}
                        </div>
                    </div>
                </span>

                <span class="p-no-float-label relocate" v-if="relocate">
                    <h5>
                        {{t("job.preferences.choose.move.to.which.city")}}
                        <Tooltip :message="t('job.preferences.choose.move.to.which.city.tooltip')" />
                    </h5>

                    <ul class="tags" data-cy="profile-edit-relocate-options"
                        v-if="computedRelocations && computedRelocations.length > 0">
                        <li v-for="option in computedRelocations" :key="option.name">
                            {{option.name}}
                            <Button icon="pi pi-times"
                                    class="p-button-rounded p-button-outlined"
                                    data-cy="profile-edit-relocate-option"
                                    @click="handleRelocationOptionRemoval($event, option)" />
                        </li>
                    </ul>
                    <Dropdown v-model="selectedRelocationCity"
                              optionLabel="name"
                              @change="relocationCitySelected"
                              display="none"
                              ref="relocationCityDropDown"
                              data-cy="profile-edit-relocate-dropdown"
                              :filter="true"
                              :autoFilterFocus="true"
                              filterIcon="pi pi-search"
                              :showToggleAll="false"
                              :options="relocationOptions" />
                    <label>{{t("job.preferences.choose.move.to.which.city.add")}}</label>
                </span>
                <small class="p-error p-mt-0"
                       v-if=" !hasSelectedCityOrState && relocate && submitted">
                    {{t("Eingabe wird benötigt")}}
                </small>
            </div>
        </div>
        <div class="call-to-action mobile-bottom-nav" v-if="showButtons">
            <Button data-cy="button-profile-prev"
                    class="button-secondary button-back p-pl-3"
                    @click="prev()">
                <i class="pi pi-angle-left" />{{t("back")}}
            </Button>
            <Button data-cy="button-profile-next"
                    class="button-secondary button-next p-pr-3"
                    @click="next()">
                {{t("continue")}} <i class="pi pi-angle-right" />
            </Button>
        </div>
    </div>
</template>
<script lang="ts">
    import { WillingnessToChange } from "@/models/WillingnessToChange";

    type RelocationOption = {
        id: number;
        name: string;
        isCity: boolean;
    };
    type FulltimeOption = {
        name: string;
        value: number;
        innerValue: boolean | null;
    };
    type ChangeOption = {
        name: string;
        innerValue: WillingnessToChange;
    };
</script>
<script lang="ts" setup>
    import { defineProps, ref, defineEmits, computed, watch, onMounted } from "vue";
    import { useI18n } from "vue-i18n";
    import useVuelidate from "@vuelidate/core";
    import { helpers, numeric } from "@vuelidate/validators";
    import { createChangeMotivationService } from "@/services/changeMotivationService";

    import Dropdown from "primevue/dropdown";
    import MultiSelect from "primevue/multiselect";
    import RadioButton from "primevue/radiobutton";
    import Slider from "primevue/slider";

    import Tooltip from "../Tooltip.vue"

    import { BrandId } from "@/models/BrandIds";
    import { CandidateProfile } from "@/models/CandidateProfile";
    import { City } from "@/models/City";
    import { FederalState } from "@/models/FederalState";
    import { HomeOffice } from "@/models/HomeOffice";
    import { ChangeMotivation } from "../../models/ChangeMotivation";

    const emit = defineEmits(["next", "previous"]);
    const props = defineProps<{
        isViewing: boolean;
        candidate: CandidateProfile;
        knownCities: City[];
        federalStates: FederalState[];
        showButtons: boolean;
        disableValidation: boolean;
    }>();

    const { t } = useI18n();
    const editableCandidate = ref(props.candidate);

    const rules = {
        remote: {},
        willingnessToChange: {
            numeric: helpers.withMessage(t("validate.numeric.error"), numeric),
        },
        changeMotivations: {},
        fullTime: {},
    };

    const v$ = useVuelidate(rules, editableCandidate);

    const changeOptions = ref<ChangeOption[]>();
    changeOptions.value = [
        {
            value: WillingnessToChange.VeryHigh,
            name: t("profilepage.willingnesstochange.4"),
        },
        {
            value: WillingnessToChange.RatherHigh,
            name: t("profilepage.willingnesstochange.3"),
        },
        {
            value: WillingnessToChange.RatherLow,
            name: t("profilepage.willingnesstochange.2"),
        },
    ];

    const motivationOptions = ref<ChangeMotivation[]>();

    const fetchChangeMotivations = async () => {
        const service = await createChangeMotivationService();
        const response = (await service.getByBrandId(editableCandidate.value.brandId)).data;
        motivationOptions.value = response.map(x => ({ id: x.id, name: t("job.preferences.changemotivations." + x.id) }))
    }

    const relocate = ref(false);

    const updateRelocate = () => {
        if (editableCandidate.value.additionalCities.length || editableCandidate.value.reachableFederalStates.length)
            relocate.value = true;
    }

    watch(props, () => {
        updateRelocate();
    });

    watch(relocate, () => {
        if (!relocate.value) {
            editableCandidate.value.additionalCities = [];
            editableCandidate.value.reachableFederalStates = [];
        }
    });

    const fulltimeOptions = ref<FulltimeOption[]>();
    fulltimeOptions.value = [
        { value: 1, innerValue: true, name: t("fulltime") },
        { value: 2, innerValue: false, name: t("parttime.greater") },
        { value: 3, innerValue: null, name: t("parttime.lesser") },
    ] as FulltimeOption[];

    const fullTimeOptionSelection = computed({
        get: () => {
            const selected = fulltimeOptions.value?.find(
                (option) => option.innerValue == editableCandidate.value.fullTime
            );
            return selected?.value;
        },
        set: (value) => {
            if (value) {
                const selected = fulltimeOptions.value?.find(
                    (option) => option.value == value
                );
                editableCandidate.value.fullTime = selected?.innerValue;
            }
        },
    });

    const changeOptionSelection = computed({
        get: () => {
            const selected = changeOptions.value?.find(
                (option) => option.value == editableCandidate.value.willingnessToChange
            );
            return selected?.value;
        },
        set: (value) => {
            if (value) {
                const selected = changeOptions.value?.find(
                    (option) => option.value == value
                );
                editableCandidate.value.willingnessToChange = selected?.value;
            }
        },
    });

    const selectedRelocationCity = ref();
    const relocationCityDropDown = ref();

    const relocationCitySelected = () => {
        if (!selectedRelocationCity.value)
            return;
        if (selectedRelocationCity.value.isCity) {
            if (!editableCandidate.value.additionalCities.find(x => x.id == selectedRelocationCity.value.id))
                editableCandidate.value.additionalCities.push(selectedRelocationCity.value);
        } else {
            if (!editableCandidate.value.reachableFederalStates.find(x => x.id == selectedRelocationCity.value.id))
                editableCandidate.value.reachableFederalStates.push(selectedRelocationCity.value);
        }
        relocationCityDropDown.value.filterValue = "";
        selectedRelocationCity.value = "";
    }

    const remoteOptions = ref();
    remoteOptions.value = [
        { value: HomeOffice.No, name: t("job.preferences.no.remote") },
        { value: HomeOffice.Possible, name: t("job.preferences.remote.possible") },
        { value: HomeOffice.Mandatory, name: t("job.preferences.remote.required") }
    ];

    // const travelDistance = computed({
    //   get: () => {
    //     return [1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100];
    //   },
    //   set: (value) => {
    //     console.log(value);
    //   },
    // });

    const hasSelectedCityOrState = computed(() => {
        return (editableCandidate.value.additionalCities && editableCandidate.value.additionalCities.length > 0) ||
            (editableCandidate.value.reachableFederalStates && editableCandidate.value.reachableFederalStates.length > 0);
    })

    const computedRelocations = computed(
        () => {
            let result: RelocationOption[];
            result = [
                ...editableCandidate.value.additionalCities.map((city) => {
                    return { id: city.id, name: city.name, isCity: true };
                }),
                ...editableCandidate.value.reachableFederalStates.map((state) => {
                    return { id: state.id, name: state.name, isCity: false };
                }),
            ];
            return result;
        }
    );

    const relocationOptions = ref([] as RelocationOption[]);
    props.knownCities.forEach((city) =>
        relocationOptions.value.push({ id: city.id, name: city.name, isCity: true })
    );

    props.federalStates.forEach((state) => {
        if (!relocationOptions.value.find(x => x.name == state.name))
            relocationOptions.value.push({
                id: state.id,
                name: state.name,
                isCity: false,
            });
    }
    );

    const handleRelocationOptionRemoval = async (
        event,
        option: RelocationOption
    ) => {
        if (option.isCity) {
            editableCandidate.value.additionalCities =
                editableCandidate.value.additionalCities.filter(
                    (city) => city.id != option.id
                );
        } else {
            editableCandidate.value.reachableFederalStates =
                editableCandidate.value.reachableFederalStates.filter(
                    (state) => state.id != option.id
                );
        }
    };

    const handleHomeOfficeRemoval = async (event, option: HomeOffice) => {
        if (editableCandidate.value.remote) {
            editableCandidate.value.remote = editableCandidate.value.remote.filter(
                (e) => e != option
            );
        }
    };

    const handleMotivationRemoval = async (event, option: ChangeMotivation) => {
        if (editableCandidate.value.changeMotivations) {
            editableCandidate.value.changeMotivations = editableCandidate.value.changeMotivations.filter(
                (e) => e != option
            );
        }
    };

    const getDisplayFriendlyRemote = (remote: HomeOffice) => {
        if (remote == HomeOffice.No)
            return t("job.preferences.no.remote")

        if (remote == HomeOffice.Possible)
            return t("job.preferences.remote.possible");

        if (remote == HomeOffice.Mandatory)
            return t("job.preferences.remote.required");

        return "";
    };

    const getDisplayFriendlyFulltime = (value: boolean | null | undefined) => {
        let fullTime = value == undefined ? editableCandidate.value.fullTime : value;

        if (fullTime == null) {
            return t("parttime.lesser");
        }

        return fullTime ? t("fulltime") : t("parttime.greater");
    };

    const getDisplayFriendlyWillingnessToChange = () => {
        let ret = "";
        switch (editableCandidate.value.willingnessToChange) {
            case 0:
                ret = "";
                break;
            case 2:
                ret = t("profilepage.willingnesstochange.2");
                break;
            case 3:
                ret = t("profilepage.willingnesstochange.3");
                break;
            case 4:
                ret = t("profilepage.willingnesstochange.4");
                break;
            default:
                ret =
                    "Unknown WillingnessToChange: " +
                    editableCandidate.value.willingnessToChange +
                    ".";
                break;
        }

        return ret;
    };

    const submitted = ref(false);
    const validate = () => {
        submitted.value = true;
        v$.value.$touch();
        let error = false;
        if (v$.value.$invalid) {
            document.getElementById(v$.value.$errors[0].$property)?.focus();
            document.getElementById(v$.value.$errors[0].$property)?.scrollIntoView();
            error = v$.value.$error || error;
        }
        return error;
    };
    const next = () => {
        if (!validate()) {
            emit("next", editableCandidate.value);
        }
    };

    const prev = () => {
        emit("previous", editableCandidate.value);
    };

    const validateSlider = (data) => {
        editableCandidate.value.searchRadius = Math.ceil(data);
    };

    onMounted(async () => {
        await fetchChangeMotivations();
        updateRelocate();
    })
</script>

